import {
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  SxProps,
  useMediaQuery,
  useTheme
} from '@mui/material';

import CloseIcon from '@/shared/ui/icons/Close.svg';

interface IProps {
  onClose: () => void;
  dialogContentSx?: SxProps;
}

/**
 * Потолок высоты bottom-sheet на телефоне (< sm), dvh. Единственный источник:
 * его же читает бюджет бокса камеры (`features/camera/lib/captureBoxSize.ts`).
 */
export const MODAL_SHEET_MAX_DVH = 90;

/**
 * Базовое модальное окно
 * @param root0 Пропсы
 * @param root0.onClose - Колбэк вызываемый при закрытии модалки
 * @param root0.children - Контент модалки
 * @param root0.dialogContentSx - Стили для контента модалки
 * @param root0.open - Открыта ли модалка
 * @param root0.sx - Стили Dialog поверх базовых
 */
export const ModalBase = ({
  onClose,
  open,
  children,
  dialogContentSx,
  sx,
  ...otherProps
}: IProps & DialogProps) => {
  const { shape } = useTheme();
  // На телефоне (< sm) диалог превращается в bottom-sheet: прижат к низу, во всю
  // ширину, растёт вверх по контенту (высота по контенту, максимум 90dvh —
  // dvh, чтобы адресная строка мобильного браузера не ломала высоту).
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // sx вызывающего дополняет, а не заменяет bottom-sheet-выравнивание
      sx={[
        isMobile
          ? { '& .MuiDialog-container': { alignItems: 'flex-end' } }
          : {},
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
      slotProps={{
        paper: {
          className: isMobile ? 'modal-bottom-sheet' : 'modal-centered',
          sx: {
            boxShadow: 'none',
            maxWidth: '100% !important',
            ...(isMobile
              ? {
                  m: 0,
                  width: '100%',
                  maxHeight: `${MODAL_SHEET_MAX_DVH}dvh`,
                  borderRadius: `${shape.borderL} ${shape.borderL} 0 0`
                }
              : { borderRadius: shape.borderL })
          }
        }
      }}
      onClick={e => {
        e.stopPropagation(); // без этого клик внутри модалки будет триггерить клики компонента, в котором находится модалка
      }}
      {...otherProps}
    >
      <DialogContent
        sx={{
          py: 6,
          px: { xs: 5, sm: 8 },
          width: { sm: 500 },
          maxWidth: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 6,
          ...dialogContentSx
        }}
      >
        {children}

        <IconButton
          aria-label='Закрыть'
          sx={{ position: 'absolute', top: 24, right: 32, padding: '5px' }}
          onClick={onClose}
        >
          <CloseIcon width={24} height={24} />
        </IconButton>
      </DialogContent>
    </Dialog>
  );
};
